import React, {useCallback, useEffect, useState} from 'react';
import createAxiosInstance from "../support/AxiosSupport";
import {useToast} from '../context/ToastContext';
import {formatDate, formatTime} from '../utils/dateTime';
import {TrashIcon} from './icons';
import Spinner from "./Spinner";
import {LIFECYCLE_LABELS, ItemLifecycleStatus} from '../support/itemLifecycle';
import {disposalSourceLabel} from '../support/disposalSources';

/** Типы записей ленты (ItemTimelineEntryDto.Type бэкенда). */
type EntryType = 'WEAR' | 'ADDED' | 'TRASHED' | 'DISPOSED';

interface TimelineEntry {
    /** Задан только у носки — её можно отменить. */
    id?: string | null;
    type: EntryType;
    at: string;
    /** Способ выбытия (код ItemLifecycleStatus); только у DISPOSED и только если известен. */
    method?: string | null;
    /** Источник решения (код DeclutterOutcomeSource); null — вещь выбыла мимо разбора. */
    source?: string | null;
}

interface WearEvent {
    id?: string | null;
    wornAt: string;
}

interface History {
    itemId: string;
    totalEvents: number;
    events: WearEvent[];
    timeline?: TimelineEntry[];
}

interface Props {
    itemId: string | undefined;
    ownerId: string | null;
    reloadKey?: number;
    /** Право EDIT_ITEMS — только с ним показываем «Удалить» у носки. */
    canEdit?: boolean;
    /** Вызывается после успешной отмены носки: родитель обновляет счётчики вещи. */
    onWearDeleted?: () => void;
}

const ENTRY_LABEL: Record<EntryType, string> = {
    WEAR: 'Носка',
    ADDED: 'Добавлена в гардероб',
    TRASHED: 'Решено расстаться — ждёт передачи',
    DISPOSED: 'Выбыла из гардероба',
};

/**
 * Уточнение события: у выбытия — способ и источник решения (D13). Архив знал, что вещь
 * выброшена, а история молчала: «Выбыла из гардероба» ничем не отличалось от «отдана подруге».
 * Чего нет — того не пишем: вещь могла выбыть мимо разбора, и источник тогда неизвестен.
 */
function entryDetail(entry: TimelineEntry): string | null {
    if (entry.type !== 'DISPOSED') {
        return null;
    }
    const parts = [
        entry.method ? LIFECYCLE_LABELS[entry.method as ItemLifecycleStatus] ?? entry.method : null,
        disposalSourceLabel(entry.source),
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(' · ') : null;
}

/** Лента вещи: носки и переводы жизненного цикла одним списком, от новых к старым. */
const WearHistory: React.FC<Props> = ({itemId, ownerId, reloadKey, canEdit = true, onWearDeleted}) => {
    const {showToast} = useToast();
    const [data, setData] = useState<History | null>(null);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    /** Инкремент после отмены носки — перезагружает ленту, не трогая пропсы родителя. */
    const [localReload, setLocalReload] = useState(0);

    useEffect(() => {
        if (!itemId) return;
        let cancelled = false;
        setLoading(true);
        setFailed(false);
        (async () => {
            try {
                const api = await createAxiosInstance();
                const params = ownerId ? {ownerId} : {};
                const res = await api.get<History>(`/wear-history/${itemId}`, {params});
                if (!cancelled) setData(res.data);
            } catch {
                if (!cancelled) setFailed(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [itemId, ownerId, reloadKey, localReload]);

    const deleteWear = useCallback(async (eventId: string) => {
        setDeletingId(eventId);
        try {
            const api = await createAxiosInstance();
            const params = ownerId ? {ownerId} : {};
            await api.delete(`/wear-history/${eventId}`, {params});
            showToast('Носка отменена', 'success');
            setLocalReload(v => v + 1);
            onWearDeleted?.();
        } catch {
            showToast('Не удалось отменить носку', 'error');
        } finally {
            setDeletingId(null);
        }
    }, [ownerId, showToast, onWearDeleted]);

    // Лента: с бэкенда — timeline; если его нет (старый ответ/кэш), собираем из носок.
    const entries: TimelineEntry[] = data?.timeline?.length
        ? data.timeline
        : (data?.events ?? []).map(e => ({id: e.id, type: 'WEAR' as EntryType, at: e.wornAt}));

    if (loading) {
        return (
            <div className="flex justify-center py-8">
                <Spinner/>
            </div>
        );
    }

    if (failed) {
        return (
            <p className="text-sm text-red-600 dark:text-red-400">Не удалось загрузить историю.</p>
        );
    }

    if (!data || entries.length === 0) {
        return (
            <p className="text-sm text-gray-500 dark:text-gray-400">История вещи пуста.</p>
        );
    }

    return (
        <section data-testid="wear-history">
            {/* D13: заголовок обещал носки, а внутри были добавление, решение и выбытие.
                Счётчик носок остался — но отдельной строкой, а не в названии ленты. */}
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                История вещи
            </h2>
            <p className="mb-4 text-xs text-gray-500 dark:text-gray-400" data-testid="wear-history-count">
                Носок: <span className="tabular-nums">{data.totalEvents}</span>
            </p>
            <ul className="divide-y divide-gray-200 dark:divide-ink-700" data-testid="wear-history-list">
                {entries.map((e, idx) => (
                    <li
                        key={e.id ?? `${e.type}-${e.at}-${idx}`}
                        data-testid={e.type === 'WEAR' ? 'wear-history-wear' : 'wear-history-lifecycle'}
                        className="flex items-center justify-between gap-3 py-2"
                    >
                        <span className="min-w-0">
                            <span className="text-sm text-gray-900 dark:text-gray-100">{ENTRY_LABEL[e.type]}</span>
                            {entryDetail(e) && (
                                <span className="block text-xs text-gray-600 dark:text-gray-300"
                                      data-testid="wear-history-detail">
                                    {entryDetail(e)}
                                </span>
                            )}
                            <span className="block text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                                {formatDate(e.at, {day: '2-digit', month: '2-digit', year: 'numeric'}, '')}
                                {formatTime(e.at) && ` · ${formatTime(e.at)}`}
                            </span>
                        </span>
                        {canEdit && e.type === 'WEAR' && e.id && (
                            <button
                                type="button"
                                disabled={deletingId === e.id}
                                onClick={() => deleteWear(e.id as string)}
                                data-testid={`wear-delete-${e.id}`}
                                aria-label="Удалить носку"
                                title="Удалить носку"
                                className="shrink-0 inline-flex items-center justify-center gap-1 min-w-[44px] min-h-[44px] px-2
                                    rounded-xl text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-ink-700
                                    hover:bg-gray-100 dark:hover:bg-ink-700 disabled:opacity-50 transition-colors"
                            >
                                <TrashIcon className="w-4 h-4"/>
                                <span className="text-xs">Удалить</span>
                            </button>
                        )}
                    </li>
                ))}
            </ul>
        </section>
    );
};

export default WearHistory;
